import React, { useState, useCallback, useEffect } from 'react';
import { Box, IconButton, Typography, Avatar } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface ImageUploadProps {
  initialImage?: string;
  onChange: (file: File | null) => void;
  onDelete?: () => void;
  label?: string;
  disabled?: boolean;
  previewSize?: {
    width: number;
    height: number;
  };
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  initialImage,
  onChange,
  onDelete,
  label = 'Subir imagen',
  disabled = false,
  previewSize = { width: 270, height: 135 }, // Reduced by 10% from 300x150
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(initialImage);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Update preview URL when initialImage prop changes
  useEffect(() => {
    setPreviewUrl(initialImage);
  }, [initialImage]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Por favor, sube un archivo de imagen válido');
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no puede ser mayor a 5MB');
      return;
    }

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
      onChange(file);
    };
    reader.readAsDataURL(file);
  }, [onChange]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewUrl(undefined);
    onChange(null);
    if (onDelete) onDelete();
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChange, onDelete]);

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.7 : 1,
      }}
      onClick={handleClick}
    >
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        disabled={disabled}
      />
      
      <Box
        sx={{
          width: previewSize.width,
          height: previewSize.height,
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'row', // Changed to row for horizontal layout
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          position: 'relative',
          bgcolor: 'background.paper',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        {previewUrl ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              position: 'relative',
              '&:hover .delete-button': {
                opacity: 1,
              },
            }}
          >
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                borderRadius: 8,
              }}
            />
            <IconButton
              className="delete-button"
              onClick={handleDelete}
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                },
                opacity: 0,
                transition: 'opacity 0.2s',
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row', // Changed to row for horizontal layout
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
              textAlign: 'center',
              p: 2,
              gap: 2, // Add some space between icon and text
            }}
          >
            <CloudUploadIcon fontSize="large" color="action" />
            <Typography variant="body2" color="textSecondary">
              {label}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ImageUpload;
